import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';

export function EvolutionPage() {
  const { context } = usePilotContext();
  const [playbook, setPlaybook] = useState(null);
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!context.customer_id) return;
    try {
      const [pb, b] = await Promise.all([
        api.get(`/api/remediation/${context.customer_id}/playbooks/latest`).catch(() => null),
        api.get(`/api/remediation/${context.customer_id}/approval/latest`).catch(() => null),
      ]);
      setPlaybook(pb);
      setBatch(b);
    } catch (e) {
      console.error('Remediation load failed:', e);
    }
  };

  useEffect(() => { load(); }, [context.customer_id]);

  const runGenerate = async () => {
    setBusy(true);
    try {
      await api.post(`/api/remediation/${context.customer_id}/playbooks/generate`, {});
      await load();
      toast.success('Playbooks generated');
    } catch (e) { toast.error(e.message || 'Generate failed'); } finally { setBusy(false); }
  };

  const runIngest = async () => {
    setBusy(true);
    try {
      await api.post(`/api/remediation/${context.customer_id}/approval/ingest`, {});
      await load();
      toast.success('Approval queue ingested');
    } catch (e) { toast.error(e.message || 'Ingest failed'); } finally { setBusy(false); }
  };

  const act = async (type, changeId) => {
    if (!batch?.batch_id) return;
    setBusy(true);
    try {
      const endpoint = `/api/remediation/${context.customer_id}/approval/${batch.batch_id}/${type}`;
      const payload = type === 'approve'
        ? { change_id: changeId, actor: 'pilot-operator' }
        : { change_id: changeId, actor: 'pilot-operator', reason: 'pilot review reject' };
      await api.post(endpoint, payload);
      await load();
    } catch (e) { toast.error(e.message || 'Action failed'); } finally { setBusy(false); }
  };

  const pending = batch?.pending_changes || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Remediation</h1>
          <p className="text-muted-foreground">Layer 3 playbook generation and approval queue.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={runGenerate} disabled={busy}>Generate playbooks</Button>
          <Button variant="outline" onClick={runIngest} disabled={busy}>Ingest approvals</Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Playbook Set</CardTitle></CardHeader>
        <CardContent>
          {!playbook ? <p className="text-sm text-muted-foreground">No playbook set found yet.</p> : <pre className="overflow-x-auto rounded-md bg-secondary/40 p-3 text-xs">{JSON.stringify(playbook, null, 2)}</pre>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Approval Queue</CardTitle>
          <CardDescription>{batch ? `Batch ${batch.batch_id}` : 'No approval batch yet.'}</CardDescription>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? <p className="text-sm text-muted-foreground">No pending changes.</p> : (
            <div className="space-y-2">
              {pending.map((c) => (
                <div key={c.change_id} className="rounded-md border border-border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <Badge variant="outline">{c.change_id}</Badge>
                    <span className="text-xs text-muted-foreground">{c.action || 'change'}</span>
                  </div>
                  <p className="mb-3 text-sm text-muted-foreground">{c.description || c.command || 'No description provided.'}</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => act('approve', c.change_id)} disabled={busy}><CheckCircle2 className="mr-1 h-4 w-4" />Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => act('reject', c.change_id)} disabled={busy}><XCircle className="mr-1 h-4 w-4" />Reject</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
